import type { ConfigLayerCapabilityOptions } from './ConfigLayerCapability'
import type { ConfigLayerOptions } from '../ConfigLayer'

import * as PoziUtils from 'api/utils'
import type { StyleGeoStylerOptions } from 'config/catalog/StyleGeoStyler'
import { StyleGeoStyler } from 'config/catalog/StyleGeoStyler'

import KeywordsParser from './KeywordsParser'
import { ConfigLayerLoaderType } from '../ConfigLayerLoaderType'
import { ConfigLayerType } from '../ConfigLayerType'

export interface ConfigLayerWFSOptions extends ConfigLayerOptions {
  type: ConfigLayerType.GEOJSON,
  config: {
    spatial: {
      url: string
      proxyUrl?: string
      loader: 'flatgeobuf' | ConfigLayerLoaderType.GEOJSON | 'gml'
      outputFormats?: Record<string, string>
      params: ConfigLayerWFSParams
      projection?: string
    }
  }
  styleGeoStyler?: StyleGeoStylerOptions
  about: {
    source: string
    organisation: string
    url?: string
  }
  legend: string
}

interface ConfigLayerWFSParams {
  SERVICE: 'WFS'
  VERSION: '1.1.0'
  REQUEST: 'GetFeature'
  SRSNAME: 'EPSG:4326'
  TYPENAME: string
  MAXFEATURES?: 10
  OUTPUTFORMAT: 'application/json'
  CQL_FILTER?: string
}

export class ConfigLayerWFS {
  static getLayer (capabilityOptions: ConfigLayerCapabilityOptions, wfsCapability: Element, layer: Element, index: number): ConfigLayerWFSOptions {
    const catalogueTitle = capabilityOptions.title
    const layerTitle = this.getTitle(capabilityOptions, index, layer)
    const titleid = this.getTitleId(capabilityOptions, index, layerTitle)
    const source = this.getSource(layer)
    const name = this.getName(layer)
    const legend = this.getLegend(capabilityOptions)
    const styleGeoStyler = this.getStyleGeoStyler(capabilityOptions, layerTitle)
    const opacity = this.getOpacity(capabilityOptions)

    let url: string

    // If the catalogue has been marked as static, we'll derive the path to the data
    // from the catalogue name (folder), layer title (file base name) and loader (file extension)
    const isStatic = !!capabilityOptions.static
    if (isStatic) {
      // console.debug('WFS static', capabilityOptions)
      const catalogueUrl = capabilityOptions.config?.spatial?.url || ''
      const baseUrl = catalogueUrl.replace(/(.+)\..*/, '$1') // remove '.xml' and evertyhing after that from URL
      const extension = 'json' // TODO: make dynamic
      url = `${baseUrl}/${layerTitle}.${extension}`
      console.debug('WFS static url', url)
    } else {
      url = this.getUrl(wfsCapability)
    }

    // Use proxy URL if set in capabilities config
    const proxyUrl = PoziUtils.getLayerProxyUrl(capabilityOptions)

    const organisation = this.getOrganisation(wfsCapability)
    const aboutUrl = this.getAboutUrl(wfsCapability)
    const keywords = this.getKeywords(layer)
    const configFromKeywords = this.getConfigFromKeywords(titleid, keywords)

    // Combine layer params from capability and keyword options
    const params = isStatic
      ? {}
      : {
          ...this.getParams(capabilityOptions, name),
          ...configFromKeywords.parameter
        }

    // Remove 'parameter' from keywordOptions now that we have used it
    delete configFromKeywords.parameter

    // Use projection from catalogue configuration, if provided
    const projection = capabilityOptions.config?.spatial?.projection

    // Use available WFS output formats from catalogue configuration, if provided
    const outputFormats = capabilityOptions.outputFormats

    // console.debug('WFS Layer outputFormats', titleid, outputFormats, capabilityOptions)

    const configLayerWFSOptions: ConfigLayerWFSOptions = {
      type: ConfigLayerType.GEOJSON,
      group: capabilityOptions.group,
      title: layerTitle,
      titleid,
      groupid: capabilityOptions.groupid,
      config: {
        spatial: {
          url,
          loader: projection ? 'gml' : ConfigLayerLoaderType.GEOJSON,
          outputFormats,
          params,
          projection,
          proxyUrl
        }
      },
      configFromKeywords,
      about: {
        source,
        organisation,
        url: aboutUrl
      },
      legend,
      linkedLayers: capabilityOptions.linkedLayers,
      opacity,
      localDataSource: capabilityOptions.localDataSource,
      serverType: capabilityOptions.serverType,
      showInLayerControl: capabilityOptions.showInLayerControl,
      showLegend: capabilityOptions.showLegend,

      // Merge in any settings from the keyword option to override the above
      ...configFromKeywords
    }
    if (styleGeoStyler) {
      configLayerWFSOptions.styleGeoStyler = styleGeoStyler
    }

    // console.debug('ConfigLayerWFS - getLayer layerConf', titleid, configLayerWFSOptions)

    return configLayerWFSOptions
  }

  private static getConfigFromKeywords (titleid: string, keywords: string[]) {
    // console.debug('WFS getConfigFromKeywords', keywords)

    // Keyword options are special config settings set in the keywords of a layer
    // since that currently is the only way to configure overrides in QGIS
    const configOptions = KeywordsParser.getOptions(keywords)

    // console.debug('WFS getConfigFromKeywords - keyword options', titleid, configOptions)

    return configOptions
  }

  private static getKeywords (layer: any) {
    const keywords = [...layer.getElementsByTagName('ows:Keyword')].map(keyword => keyword.textContent.replace(/^ows:/, ''))

    // console.debug('WFS getKeywords', keywords)

    return keywords
  }

  private static getParams (capabilityOptions: ConfigLayerCapabilityOptions, name: string): ConfigLayerWFSParams {
    // Get config-provided params
    let capabilityParams = capabilityOptions?.config?.spatial?.params || []

    // Make any config-provided params all UPPERCASE
    capabilityParams = Object.fromEntries(Object.entries(capabilityParams)
      .map(([key, value]) => [key.toUpperCase(), value]))

    // If a projection has been defined, we will automatically request the WFS information in
    // that projection in GML instead of GeoJSON (as QGIS does only deliver GeoJSON in EPSG:4326)
    const projection = capabilityOptions?.config?.spatial?.projection

    const params = {
      SERVICE: 'WFS',
      VERSION: '1.1.0',
      REQUEST: 'GetFeature',
      SRSNAME: projection || 'EPSG:4326',
      OUTPUTFORMAT: projection ? 'GML3' : 'application/json',
      TYPENAME: name,

      // Merge standard params and config-defined params
      ...capabilityParams
    } as ConfigLayerWFSParams

    // console.debug('getParams', params)

    return params
  }

  private static getTitle (capabilityOptions: ConfigLayerCapabilityOptions, index: number, layer: Element): string {
    const title: string = layer?.getElementsByTagName('Title')?.[0]?.textContent ?? `${capabilityOptions.title}_${index}`
    // console.debug('title: ', title)
    return title
  }

  private static getTitleId (capabilityOptions: ConfigLayerCapabilityOptions, index: number, title: string): string {
    const titleid: string = PoziUtils.getPoziIDFromString(title) ?? `${capabilityOptions.title}_${index}`
    // console.debug('titleid: ', titleid)
    return titleid
  }

  private static getSource (layer: Element): string {
    const source: string = layer.getElementsByTagName('Abstract')?.[0]?.textContent ?? ''
    // console.debug('source: ', source)
    return source
  }

  private static getName (layer: Element): string {
    const name: string = layer.getElementsByTagName('Name')?.[0]?.textContent ?? ''
    // console.debug('name: ', name)
    return name
  }

  private static getStyleGeoStyler (capabilityOptions: ConfigLayerCapabilityOptions, title: string) {
    return StyleGeoStyler.getStyleGeoStyler(capabilityOptions, title)
  }

  private static getLegend (capabilityOptions: ConfigLayerCapabilityOptions): string {
    const legend = capabilityOptions?.legend || ''
    // console.debug('getLegend: ', legend)
    return legend
  }

  private static getOpacity (capabilityOptions: ConfigLayerCapabilityOptions) {
    const opacity = capabilityOptions.opacity ?? 1

    // console.debug('getOpacity: ', opacity, layer)
    return opacity
  }

  private static getUrl (wfsCapabilities: Element): string {
    const url = wfsCapabilities.getElementsByTagName('ows:Get')?.[0]?.attributes.getNamedItem('xlink:href')?.value ?? ''

    // console.debug('url: ', url)
    return url
  }

  private static getOrganisation (wfsCapabilities: Element): string {
    const organisation = wfsCapabilities.getElementsByTagName('ows:ProviderName')?.[0]?.textContent ?? ''
    // console.debug('organisation: ', organisation)
    return organisation
  }

  private static getAboutUrl (wfsCapabilities: Element): string {
    // FIXME: This should be accessed from the layer, not the WFS Capabilities
    const aboutUrl = wfsCapabilities.getElementsByTagName('MetadataURL')?.[0]?.attributes?.getNamedItem('xlink:href')?.value ?? ''
    // console.debug('metadataUrl: ', aboutUrl)
    return aboutUrl
  }
}
